import classNames from 'classnames'
import { FundingRound as FundingRoundType, Investment } from 'entities-next/fundingRounds'
import { noop } from 'lodash'
import PencilIcon from 'mdi-react/PencilIcon'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { FormViews, useEdition } from 'utils-next/forms'
import { ExtendableStyles, Testable } from 'utils-next/types'

import { Card, CardContent, IconButton, Theme } from '@mui/material'

import { FundingRoundEdit, FundingRoundView } from './components'

export type FundingRoundProps = ExtendableStyles &
  Testable & {
    data: FundingRoundType
    editable?: boolean
    initialView?: FormViews
    onDelete?: () => void
    onUpdate?: (data: FundingRoundType) => Promise<any> | any
  }

export const FundingRound = React.memo(
  ({
    className,
    data,
    editable = false,
    initialView = FormViews.view,
    onDelete = noop,
    onUpdate = noop,
    ...otherProps
  }: FundingRoundProps) => {
    const { classes } = useStyles()
    const { showEditionButton, enableEdition, disableEdition, view } = useEdition({
      editable,
      initialView,
    })

    const handleFormSubmit = async (data: FundingRoundType) => {
      await onUpdate(data)
      disableEdition()
    }

    const handleInvestmentUpdate = async (investments: Investment[]) => {
      await onUpdate({ ...data, investments })
    }

    return (
      <Card className={classNames(className, classes.container)} {...otherProps}>
        {showEditionButton && (
          <IconButton aria-label="edit" onClick={enableEdition} className={classes.editButton}>
            <PencilIcon />
          </IconButton>
        )}
        <CardContent>
          {view === FormViews.edit && (
            <FundingRoundEdit
              data={data}
              onDelete={onDelete}
              onUpdate={handleFormSubmit}
              onUpdateInvestments={handleInvestmentUpdate}
            />
          )}
          {view === FormViews.view && <FundingRoundView data={data} />}
        </CardContent>
      </Card>
    )
  }
)

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  container: {
    position: 'relative',
  },
  editButton: {
    position: 'absolute',
    right: spacing(),
    top: spacing(),
    zIndex: 2,
  },
}))
